// The «Смета» screen reads like the ЛСР: the same order, numbers, counts and wording.
import type { Schema } from "./api";
import { normCode } from "./normCode.ts";

type Position = Schema["EstimatePosition"];
type Resource = Position["calculation"]["resources"][number];
export type Extra = Record<string, unknown>;
export type EstimateItem = { kind: "position"; row: Position } | { kind: "extra"; extra: Extra };

const number = (value: unknown) => typeof value === "number" ? value : null;

/** Positions and separate materials in the order of the ЛСР: each material right after the
 * position it follows there. The server sends both lists in that order with their № п/п; a work
 * without a norm has no number and keeps its place. Snapshots without numbers keep their order. */
export function lsrItems(rows: Position[], extras: Extra[]): EstimateItem[] {
  const pending = [...extras].sort((a, b) => (number(a.lsr_number) ?? Infinity) - (number(b.lsr_number) ?? Infinity));
  const items: EstimateItem[] = [];
  const take = (below: number) => {
    while (pending.length && (number(pending[0].lsr_number) ?? Infinity) < below) items.push({ kind: "extra", extra: pending.shift()! });
  };
  rows.forEach((row, index) => {
    const own = row.lsr_number ?? null;
    if (own != null) take(own);
    items.push({ kind: "position", row });
    if (own != null) {
      const next = rows.slice(index + 1).find(other => other.lsr_number != null)?.lsr_number ?? Infinity;
      take(next);
    }
  });
  pending.forEach(extra => items.push({ kind: "extra", extra }));
  return items;
}

/** «ФСБЦ-11.3.01.02-0010», a received quote's «ТЦ_…» code, or «КАЦ» while no code is known. */
export function extraCode(extra: Extra): string {
  const code = typeof extra.code === "string" ? extra.code : "";
  return !code ? "КАЦ" : code.startsWith("ТЦ") ? code : `ФСБЦ-${code}`;
}

/** What a «Н» / «П,Н» row means for the estimate: where it is priced, or that it is not yet. */
export function abstractText(row: Position, resource: Resource, extras: Extra[]): string {
  if (!resource.priced_separately) return "Отдельной позиции ФСБЦ/КАЦ нет — итог сметы не определён, пока её не добавят в параметрах сметы.";
  const covering = extras.find(extra => extra.covers_application_id === row.basis.application_id
    && extra.covers_code === resource.code && !extra.excludes);
  const place = number(covering?.lsr_number);
  return `Не входит в стоимость позиции: оценивается отдельной позицией${place != null ? ` ЛСР поз. ${place}` : ""}.`;
}

/** «ЛСР поз. 5 · ВОР п. 3»: where the row is in the ЛСР and in the ВОР of the package. */
export function placeText(row: Position): string {
  const vor = [...new Set(row.basis.vor_numbers.filter((value): value is number => value != null))];
  return [row.lsr_number != null ? `ЛСР поз. ${row.lsr_number}` : row.basis.norm_id ? "" : "Без номера в ЛСР: норма не выбрана",
    vor.length ? `ВОР п. ${vor.join(", ")}` : ""].filter(Boolean).join(" · ");
}

/** The counts the ЛСР prints; snapshots saved before they existed keep the old count. */
export function countsText(counts: Record<string, number>): string {
  const unapproved = `Неподтверждённых работ: ${counts.unapproved ?? 0}.`;
  if (counts.lsr_positions == null) return `Рассчитано позиций: ${counts.rows - counts.unpriced} из ${counts.rows}. ${unapproved}`;
  return [`Позиций ЛСР: ${counts.lsr_positions}, из них без итога: ${counts.lsr_incomplete ?? 0}.`,
    counts.without_norm ? `Работ без нормы: ${counts.without_norm}.` : "", unapproved].filter(Boolean).join(" ");
}

/** A position's reasons, each once: the same issue reaches it from the work and the calculation. */
export function uniqueReasons(...lists: string[][]): string[] {
  return [...new Set(lists.flat().filter(Boolean))];
}

type Estimate = { rows: Position[]; additional_resources: Extra[] };

/** Where the ЛСР prices each ВОР work: its norm positions and the separate positions of its materials. */
export function lsrPlaces(estimate: Estimate): Record<string, string> {
  const places: Record<string, string[]> = {};
  const add = (work: string, text: string) => { (places[work] ??= []).push(text); };
  for (const row of estimate.rows) {
    if (row.lsr_number == null) continue;
    for (const work of row.basis.work_ids) add(work, `поз. ${row.lsr_number} (${normCode(row.lsr_code || row.basis.norm_code)})`);
  }
  for (const extra of estimate.additional_resources) {
    if (typeof extra.lsr_number === "number" && typeof extra.work_id === "string") add(extra.work_id, `поз. ${extra.lsr_number} (${extraCode(extra)})`);
  }
  return Object.fromEntries(Object.entries(places).map(([work, list]) => [work, `ЛСР ${[...new Set(list)].join(", ")}`]));
}

/** What the ЛСР still needs priced: КАЦ positions without a quote and «Н» rows without a position. */
export function lsrOpenPrices(estimate: Estimate) {
  const kac = estimate.additional_resources.filter(extra => !extra.code && !extra.excludes && extra.amount == null);
  const unpriced = estimate.rows.flatMap(row => row.calculation.resources
    .filter(resource => resource.kind === "abstract" && !resource.priced_separately)
    .map(resource => ({ row, resource })));
  return { kac, unpriced };
}
